import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel, Stat, Severity, Money } from '../components/ui'
import { money, number } from '../lib/format'

type SeverityLevel = 'low' | 'medium' | 'high' | 'critical'
type ThresholdValue = number | string | boolean

interface RuleDefinition {
  code: string
  version: number
  title_th: string
  description_th: string
  severity_default: SeverityLevel
  thresholds: Record<string, ThresholdValue>
}

interface RegistryRow extends RuleDefinition {
  enabled: boolean
}

interface RuleRow extends RegistryRow {
  created_at: string
  findings_count: number
  amount_at_risk_satang: number
}

interface StaleRule {
  code: string
  code_version: number
  db_version: number
  fields: string[]
  row: RegistryRow
}

interface RuleDrift {
  clean: boolean
  missing: RuleDefinition[]
  stale: StaleRule[]
  extra: RegistryRow[]
  counts: { missing: number; stale: number; extra: number }
  catalogue_count: number
}

const RULES_KEY = ['rule-registry'] as const
const DRIFT_KEY = ['rule-registry', 'drift'] as const

const severityLabel: Record<SeverityLevel, string> = {
  low: 'ต่ำ', medium: 'ปานกลาง', high: 'สูง', critical: 'วิกฤต',
}

const thresholdLabel: Record<string, string> = {
  threshold_satang: 'ยอดขั้นต่ำ',
  critical_satang: 'ยอดวิกฤต',
  medium_satang: 'เกณฑ์ปานกลาง',
  high_satang: 'เกณฑ์สูง',
  high_threshold_satang: 'เกณฑ์ระดับสูง',
  min_amount_satang: 'ยอดขั้นต่ำ',
  divisor_satang: 'ตัวหารลงตัว',
  escalate_satang: 'ยอดยกระดับ',
  split_exclusion_satang: 'เกณฑ์ตัดกลุ่มแบ่งจ่าย',
  min_total_satang: 'ยอดรวมขั้นต่ำ',
  critical_total_satang: 'ยอดรวมวิกฤต',
  duplicate_window_hours: 'หน้าต่างตรวจ',
  window_hours: 'หน้าต่างเวลา',
  window_days: 'หน้าต่างตรวจ',
  business_start_hour: 'เริ่มเวลาทำการ',
  business_end_hour: 'สิ้นสุดเวลาทำการ',
  late_start_hour: 'เริ่มนอกรอบ',
  late_end_hour: 'สิ้นสุดนอกรอบ',
  min_count: 'จำนวนขั้นต่ำ',
  min_occurrences: 'จำนวนครั้งขั้นต่ำ',
  min_payees: 'ผู้รับขั้นต่ำ',
  min_tx: 'รายการขั้นต่ำ',
  min_history: 'ประวัติขั้นต่ำ',
  max_hits: 'รายการสูงสุด',
  min_share_pct: 'ส่วนแบ่งขั้นต่ำ',
  critical_share_pct: 'ส่วนแบ่งวิกฤต',
}

// The few thresholds that explain a rule best; everything else follows alphabetically.
const thresholdOrder = [
  'threshold_satang', 'min_share_pct', 'window_hours', 'duplicate_window_hours', 'window_days',
  'min_count', 'min_occurrences', 'min_payees', 'min_tx', 'min_history',
  'medium_satang', 'high_satang', 'min_amount_satang', 'escalate_satang',
]

function formatThreshold(key: string, value: ThresholdValue): string {
  if (typeof value === 'boolean') return value ? 'ใช่' : 'ไม่ใช่'
  if (typeof value === 'string') return value
  if (key.endsWith('_satang')) return money(value, { compact: true })
  if (key.endsWith('_pct')) return `${value}%`
  if (key.endsWith('_hours')) return `${value} ชม.`
  if (key.endsWith('_days')) return `${value} วัน`
  if (key.endsWith('_hour')) return `${value}:00 น.`
  return number(value)
}

function sortedThresholds(thresholds: Record<string, ThresholdValue>) {
  return Object.entries(thresholds).sort((a, b) => {
    const ai = thresholdOrder.indexOf(a[0])
    const bi = thresholdOrder.indexOf(b[0])
    const ar = ai === -1 ? thresholdOrder.length : ai
    const br = bi === -1 ? thresholdOrder.length : bi
    return ar - br || a[0].localeCompare(b[0])
  })
}

function thresholdChips(row: RegistryRow) {
  const entries = sortedThresholds(row.thresholds)
  const shown = entries.slice(0, 3)
  return (
    <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
      {shown.map(([key, value]) => (
        <span key={key} className="chip" style={{ fontSize: 10.5, height: 22, padding: '0 8px' }}
          title={`${thresholdLabel[key] ?? key}: ${formatThreshold(key, value)}`}>
          {thresholdLabel[key] ?? key} {formatThreshold(key, value)}
        </span>
      ))}
      {entries.length > shown.length ? (
        <span className="dim" style={{ fontSize: 10.5, alignSelf: 'center' }}>+{entries.length - shown.length}</span>
      ) : null}
    </div>
  )
}

export function Rules() {
  const qc = useQueryClient()
  const [severity, setSeverity] = useState<SeverityLevel | 'all'>('all')
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'on' | 'off'>('all')
  const [q, setQ] = useState('')
  const [savingCode, setSavingCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const rulesQ = useQuery({ queryKey: RULES_KEY, queryFn: () => api<RuleRow[]>('/v1/rules') })
  const driftQ = useQuery({ queryKey: DRIFT_KEY, queryFn: () => api<RuleDrift>('/v1/rules/drift') })

  const toggle = useMutation({
    mutationFn: (v: { code: string; enabled: boolean }) =>
      apiWrite<RuleRow>(`/v1/rules/${encodeURIComponent(v.code)}`, 'PATCH', { enabled: v.enabled }),
    onSuccess: (row) => {
      // Patch the cache first so the switch does not flash back to the old state on refetch.
      qc.setQueryData<RuleRow[]>(RULES_KEY, (old) =>
        old ? old.map((x) => (x.code === row.code ? { ...x, enabled: row.enabled } : x)) : old)
      setError(null)
      void qc.invalidateQueries({ queryKey: RULES_KEY })
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'บันทึกสถานะกฎไม่สำเร็จ'),
    onSettled: () => setSavingCode(null),
  })

  const sync = useMutation({
    mutationFn: () => apiWrite<{ synced: number; total: number }>('/v1/rules/sync', 'POST'),
    onSuccess: (res) => {
      setNotice(`ซิงก์ทะเบียนแล้ว ${res.synced} จาก ${res.total} กฎ`)
      setError(null)
      void qc.invalidateQueries({ queryKey: RULES_KEY })
      void qc.invalidateQueries({ queryKey: DRIFT_KEY })
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'ซิงก์ทะเบียนไม่สำเร็จ'),
  })

  const rows = rulesQ.data ?? []
  const drift = driftQ.data

  const counts = useMemo(() => {
    const enabled = rows.filter((r) => r.enabled).length
    const findings = rows.reduce((sum, r) => sum + r.findings_count, 0)
    const atRisk = rows.reduce((sum, r) => sum + r.amount_at_risk_satang, 0)
    return { total: rows.length, enabled, disabled: rows.length - enabled, findings, atRisk }
  }, [rows])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return rows.filter((r) =>
      (severity === 'all' || r.severity_default === severity) &&
      (enabledFilter === 'all' || (enabledFilter === 'on' ? r.enabled : !r.enabled)) &&
      (needle === '' ||
        r.code.toLowerCase().includes(needle) ||
        r.title_th.toLowerCase().includes(needle) ||
        r.description_th.toLowerCase().includes(needle)),
    )
  }, [rows, severity, enabledFilter, q])

  const driftCount = drift ? drift.counts.missing + drift.counts.stale + drift.counts.extra : 0
  const busy = toggle.isPending || sync.isPending

  function commit(row: RuleRow, enabled: boolean) {
    if (busy) return
    setSavingCode(row.code)
    toggle.mutate({ code: row.code, enabled })
  }

  return (
    <>
      <PageHead
        title="ทะเบียนกฎตรวจสอบ"
        sub="รหัสกฎ เวอร์ชัน เกณฑ์ที่ใช้จริง และสถานะเปิดใช้ — เทียบกับโค้ดเพื่อจับ drift อัตโนมัติ"
        actions={
          <button className="btn btn-primary" disabled={busy} onClick={() => { setNotice(null); sync.mutate() }}>
            {sync.isPending ? 'กำลังซิงก์…' : 'ซิงก์ทะเบียนจากโค้ด'}
          </button>
        }
      />

      <div className="grid-4 stagger">
        <Stat label="กฎที่ลงทะเบียน" value={counts.total} unit="กฎ" foot={`เปิดใช้ ${counts.enabled} · ปิด ${counts.disabled}`} />
        <Stat label="ข้อสังเกตจากกฎเหล่านี้" value={number(counts.findings)} unit="รายการ" foot="นับจากผลตรวจที่บันทึกไว้" />
        <Stat label="มูลค่าเสี่ยงคงค้าง" value={<Money satang={counts.atRisk} compact />} foot="ไม่รวมที่ปิดหรือตีกลับแล้ว" />
        <Stat label="ความสอดคล้องกับโค้ด" value={driftQ.isLoading ? '…' : drift?.clean ? 'ตรงกัน' : 'พบ drift'}
          unit={drift && !drift.clean ? `${driftCount} จุด` : undefined}
          foot={drift ? `โค้ด ${drift.catalogue_count} กฎ · ฐานข้อมูล ${counts.total} กฎ` : 'กำลังตรวจทะเบียน'} />
      </div>

      {drift && !drift.clean ? (
        <div className="card card-pad" style={{ marginTop: 18, borderColor: 'var(--color-amber)' }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <div className="card-label" style={{ color: 'var(--color-amber)' }}>ทะเบียนไม่ตรงกับโค้ด</div>
              <div style={{ marginTop: 6 }}>
                ขาด {drift.counts.missing} · ล้าสมัย {drift.counts.stale} · เกิน {drift.counts.extra}
              </div>
              <ul className="dim" style={{ fontSize: 12, lineHeight: 1.8, margin: '10px 0 0', paddingLeft: 18 }}>
                {drift.missing.slice(0, 4).map((r) => (
                  <li key={`m-${r.code}`}>ขาดกฎ <span className="mono">{r.code}</span> เวอร์ชัน {r.version}</li>
                ))}
                {drift.stale.slice(0, 4).map((s) => (
                  <li key={`s-${s.code}`}>
                    <span className="mono">{s.code}</span> ล้าสมัย: โค้ด v{s.code_version} · ฐานข้อมูล v{s.db_version}
                    {s.fields.length > 0 ? ` (${s.fields.join(', ')})` : ''}
                  </li>
                ))}
                {drift.extra.slice(0, 4).map((r) => (
                  <li key={`e-${r.code}`}>มีในฐานข้อมูลแต่ไม่มีในโค้ด <span className="mono">{r.code}</span> v{r.version}</li>
                ))}
              </ul>
              {driftCount > 12 ? <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>แสดงบางส่วนจากทั้งหมด {driftCount} จุด</div> : null}
            </div>
            <button className="btn btn-sm" disabled={busy} onClick={() => { setNotice(null); sync.mutate() }}>
              ซิงก์เพื่อแก้ไข
            </button>
          </div>
        </div>
      ) : drift?.clean ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <div className="row" style={{ gap: 10 }}>
            <span className="badge badge-closed">ตรงกัน</span>
            <span className="dim" style={{ fontSize: 12.5 }}>
              ทะเบียนทั้ง {counts.total} กฎตรงกับโค้ดเวอร์ชันล่าสุด ไม่พบรายการขาด ล้าสมัย หรือเกิน
            </span>
          </div>
        </div>
      ) : null}

      {notice ? <div className="card card-pad" style={{ marginTop: 14 }}><span className="dim">{notice}</span></div> : null}
      {error ? <div className="form-error" style={{ marginTop: 14 }}>{error}</div> : null}

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {(['all', 'on', 'off'] as const).map((v) => (
            <button key={v} className={`chip${enabledFilter === v ? ' chip-on' : ''}`} onClick={() => setEnabledFilter(v)}>
              {v === 'all' ? 'ทุกสถานะ' : v === 'on' ? 'เปิดใช้' : 'ปิดใช้'}
            </button>
          ))}
          <span style={{ width: 10 }} />
          <select aria-label="กรองตามหมวดกฎ" className="input" style={{ width: 150, height: 32 }} value={severity}
            onChange={(e) => setSeverity(e.target.value as SeverityLevel | 'all')}>
            <option value="all">ทุกระดับ</option>
            {(Object.keys(severityLabel) as SeverityLevel[]).map((s) => (
              <option key={s} value={s}>{severityLabel[s]}</option>
            ))}
          </select>
          <input className="input" style={{ width: 250, height: 32 }} placeholder="ค้นรหัสหรือชื่อกฎ"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <span className="dim" style={{ fontSize: 11.5, alignSelf: 'center' }}>{filtered.length} กฎ</span>
        </div>
      </div>

      {rulesQ.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}><Skel h={64} /><Skel h={64} /><Skel h={64} /></div>
      ) : rulesQ.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="form-error">โหลดทะเบียนกฎไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="card-label">ยังไม่มีทะเบียนกฎ</div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 8 }}>
            กด “ซิงก์ทะเบียนจากโค้ด” เพื่อสร้างรายการจากแคตตาล็อกทั้งหมด จากนั้นจึงเปิดหรือปิดแต่ละกฎได้
          </div>
        </div>
      ) : (
        <div className="table-wrap card" style={{ marginTop: 16 }}>
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 210 }}>รหัสกฎ</th>
                <th>ชื่อและหลักเกณฑ์</th>
                <th style={{ width: 112 }}>ระดับเริ่มต้น</th>
                <th style={{ width: 270 }}>เกณฑ์หลัก</th>
                <th style={{ width: 88 }} className="num">ข้อสังเกต</th>
                <th style={{ width: 132 }} className="num">มูลค่าเสี่ยง</th>
                <th style={{ width: 88 }}>เปิดใช้</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const pending = toggle.isPending && savingCode === row.code
                return (
                  <tr key={row.code} style={{ cursor: 'default' }}>
                    <td>
                      <div className="mono" style={{ fontSize: 12, color: 'var(--color-ivory)' }}>{row.code}</div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>เวอร์ชัน {row.version}</div>
                    </td>
                    <td>
                      <div style={{ color: 'var(--color-ivory)' }}>{row.title_th}</div>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 4, maxWidth: 560 }}>{row.description_th}</div>
                    </td>
                    <td><Severity value={row.severity_default} /></td>
                    <td>{thresholdChips(row)}</td>
                    <td className="num tnum">{number(row.findings_count)}</td>
                    <td className="num"><Money satang={row.amount_at_risk_satang} compact /></td>
                    <td>
                      <label className="row" style={{ gap: 0, cursor: busy ? 'wait' : 'pointer' }}>
                        <span style={{ position: 'relative', display: 'inline-flex', width: 36, height: 20, flex: 'none' }}>
                          <input
                            type="checkbox"
                            role="switch"
                            aria-label={`เปิดใช้งานกฎ ${row.code}`}
                            checked={row.enabled}
                            disabled={busy}
                            onChange={(e) => commit(row, e.target.checked)}
                            style={{
                              position: 'absolute', inset: 0, margin: 0, appearance: 'none', WebkitAppearance: 'none',
                              background: row.enabled ? 'var(--color-gold)' : 'var(--color-hairline-strong)',
                              border: `1px solid ${row.enabled ? 'var(--color-gold)' : 'var(--color-hairline-strong)'}`,
                              borderRadius: 'var(--radius-full)',
                              cursor: busy ? 'wait' : 'pointer',
                              opacity: busy && !pending ? 0.55 : 1,
                              transition: 'background var(--micro) var(--ease-standard)',
                            }}
                          />
                          <span aria-hidden="true" style={{
                            position: 'absolute', top: 2, left: row.enabled ? 18 : 2, width: 16, height: 16,
                            borderRadius: '50%', pointerEvents: 'none',
                            background: row.enabled ? '#1A1712' : 'var(--color-ivory-dim)',
                            transition: 'left var(--micro) var(--ease-standard)',
                          }} />
                        </span>
                      </label>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
                        {pending ? 'กำลังบันทึก…' : row.enabled ? 'เปิด' : 'ปิด'}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="dim" style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.8 }}>
        การปิดกฎมีผลกับความเชื่อถือของผลตรวจครั้งถัดไปเท่านั้น ไม่ลบข้อสังเกตที่เกิดขึ้นแล้ว
        และการซิงก์จะไม่เปิดกฎที่ถูกปิดไว้โดยพลการ
      </div>
    </>
  )
}
